import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import type { LineaPianoRead } from "@/types/m2";
import { coloreStampo, fmtMinuti, num } from "@/pages/M2/format";

// Gantt = PANORAMICA opzionale (doc #4: la vista primaria è la lista). Asse
// temporale reale, zoom + finestra navigabile, click su un blocco → dettaglio.
const DAY = 86_400_000;

type Sezione = "estrusione" | "confezionamento";

const ZOOM = [
  { key: "giorno", label: "Giorno", giorni: 1, px: 900 },
  { key: "3giorni", label: "3 giorni", giorni: 3, px: 320 },
  { key: "settimana", label: "Settimana", giorni: 7, px: 150 },
  { key: "2settimane", label: "2 settimane", giorni: 14, px: 78 },
] as const;
type ZoomKey = (typeof ZOOM)[number]["key"];

interface Blocco {
  id: string;
  lane: string;
  sezione: Sezione;
  stampo: string | null;
  code: string;
  desc: string | null;
  ordine: string | null;
  famiglia: string | null;
  t0: number;
  t1: number;
  quantita: number | null;
  um: string | null;
  durataMin: number | null;
}

interface Corsia {
  lane: string;
  nome: string;
  sezione: Sezione;
}

function tsOf(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function build(linee: LineaPianoRead[]) {
  const blocchi: Blocco[] = [];
  const corsie: Corsia[] = [];
  for (const l of linee) {
    corsie.push({ lane: l.codice_macchina, nome: l.nome ?? `Linea ${l.codice_macchina}`, sezione: l.tipo });
    for (const r of l.righe) {
      const t0 = tsOf(r.finestra_da);
      if (t0 === null) continue;
      const t1 = tsOf(r.finestra_a) ?? t0 + (r.durata_min ?? 60) * 60_000;
      blocchi.push({
        id: r.id,
        lane: l.codice_macchina,
        sezione: l.tipo,
        stampo: r.stampo_code,
        code: r.semilavorato_codice ?? r.materiale_codice ?? r.articolo_codice ?? "—",
        desc: r.descrizione,
        ordine: r.ordine_mes,
        famiglia: r.famiglia,
        t0,
        t1: Math.max(t1, t0 + 3_600_000),
        quantita: r.quantita,
        um: r.um,
        durataMin: r.durata_min,
      });
    }
  }
  const tutti = blocchi.map((b) => b.t0).concat(blocchi.map((b) => b.t1));
  const origine = tutti.length ? Math.min(...tutti) : Date.now();
  const fine = tutti.length ? Math.max(...tutti) : origine + 14 * DAY;
  return { blocchi, corsie, origine, fine };
}

function floorDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function GanttProduzione({ linee }: { linee: LineaPianoRead[] }) {
  const base = useMemo(() => build(linee), [linee]);
  const [zoomKey, setZoomKey] = useState<ZoomKey>("settimana");
  const zoom = ZOOM.find((z) => z.key === zoomKey)!;
  const ampiezza = zoom.giorni * DAY;
  const [winStart, setWinStart] = useState<number>(() => floorDay(base.origine));
  const [sel, setSel] = useState<string | null>(null);

  useEffect(() => {
    setWinStart(floorDay(base.origine));
    setSel(null);
  }, [base]);

  const winEnd = winStart + ampiezza;
  const pxDay = zoom.px;
  const trackW = zoom.giorni * pxDay;
  const ticks: number[] = [];
  for (let t = winStart; t <= winEnd + 1; t += DAY) ticks.push(t);

  const estr = base.corsie.filter((c) => c.sezione === "estrusione");
  const conf = base.corsie.filter((c) => c.sezione === "confezionamento");
  const selBlocco = sel ? base.blocchi.find((b) => b.id === sel) : null;

  function naviga(dir: -1 | 1) {
    setWinStart((s) => Math.max(floorDay(base.origine), Math.min(s + dir * ampiezza, floorDay(base.fine))));
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-lg border">
          {ZOOM.map((z) => (
            <button
              key={z.key}
              type="button"
              onClick={() => setZoomKey(z.key)}
              className={cn("px-3 py-1.5 text-xs font-medium", z.key === zoomKey ? "bg-primary text-primary-foreground" : "bg-card hover:bg-accent/40")}
            >
              {z.label}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1">
          <button type="button" onClick={() => naviga(-1)} className="rounded-md border bg-card px-2 py-1.5 text-sm hover:bg-accent/40">‹</button>
          <span className="min-w-[150px] text-center text-sm font-medium tabular-nums">{fmtRange(winStart, winEnd)}</span>
          <button type="button" onClick={() => naviga(1)} className="rounded-md border bg-card px-2 py-1.5 text-sm hover:bg-accent/40">›</button>
        </div>
        <Overview origine={base.origine} fine={base.fine} winStart={winStart} winEnd={winEnd} onJump={(t) => setWinStart(floorDay(t))} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_240px]">
        <div className="space-y-4">
          {estr.length > 0 && <SezioneGantt titolo="Estrusione" corsie={estr} blocchi={base.blocchi} ticks={ticks} winStart={winStart} pxDay={pxDay} trackW={trackW} sel={sel} onSel={setSel} />}
          {conf.length > 0 && <SezioneGantt titolo="Confezionamento" corsie={conf} blocchi={base.blocchi} ticks={ticks} winStart={winStart} pxDay={pxDay} trackW={trackW} sel={sel} onSel={setSel} />}
        </div>
        <div className="sticky top-4 h-fit rounded-lg border bg-card p-4">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Dettaglio</div>
          {selBlocco ? (
            <div className="space-y-1.5 text-[13px]">
              <div className="flex items-center gap-1.5">
                <span className="size-2.5 rounded-sm" style={{ background: coloreStampo(selBlocco.stampo).border }} />
                <span className="font-mono font-semibold">{selBlocco.code}</span>
              </div>
              <div className="text-muted-foreground">{selBlocco.desc}</div>
              <div className="text-muted-foreground">ordine {selBlocco.ordine ?? "—"}</div>
              <div className="text-muted-foreground">{selBlocco.famiglia ?? "—"} · {selBlocco.quantita != null ? num(selBlocco.quantita) : "—"} {selBlocco.um ?? ""}</div>
              <div className="text-muted-foreground">durata {fmtMinuti(selBlocco.durataMin)}</div>
              <div className="text-muted-foreground">{fmtRange(selBlocco.t0, selBlocco.t1)}</div>
            </div>
          ) : (
            <p className="text-[12px] text-muted-foreground">Clicca un blocco per i dettagli. Colore = stampo.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function SezioneGantt({
  titolo,
  corsie,
  blocchi,
  ticks,
  winStart,
  pxDay,
  trackW,
  sel,
  onSel,
}: {
  titolo: string;
  corsie: Corsia[];
  blocchi: Blocco[];
  ticks: number[];
  winStart: number;
  pxDay: number;
  trackW: number;
  sel: string | null;
  onSel: (id: string | null) => void;
}) {
  const xOf = (t: number) => ((t - winStart) / DAY) * pxDay;
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <div className="border-b bg-muted px-4 py-2.5 text-[13px] font-semibold">{titolo}</div>
      <div className="overflow-x-auto">
        <div style={{ minWidth: 118 + trackW }}>
          <div className="flex h-6 border-b">
            <div className="w-[118px] shrink-0 border-r" />
            <div className="relative flex-1" style={{ width: trackW }}>
              {ticks.map((t) => (
                <span key={t} className="absolute top-0 h-full border-l border-slate-100 pl-1 text-[10px] leading-6 text-muted-foreground" style={{ left: xOf(t) }}>
                  {fmtDay(t)}
                </span>
              ))}
            </div>
          </div>
          {corsie.map((c) => {
            const bl = blocchi.filter((b) => b.lane === c.lane).sort((a, b) => a.t0 - b.t0);
            return (
              <div key={c.lane} className="flex min-h-[48px] border-b last:border-b-0">
                <div className="flex w-[118px] shrink-0 flex-col justify-center border-r px-3">
                  <span className="text-[12.5px] font-semibold">{c.nome}</span>
                  <span className="text-[10.5px] text-muted-foreground">{c.lane}</span>
                </div>
                <div className="relative flex-1" style={{ width: trackW, minHeight: 48 }}>
                  {ticks.map((t) => (
                    <div key={t} className="absolute top-0 bottom-0 border-l border-slate-50" style={{ left: xOf(t) }} />
                  ))}
                  {bl.map((b, i) => {
                    const prev = bl[i - 1];
                    const setupPrima = c.sezione === "estrusione" && prev && prev.stampo !== b.stampo;
                    const col = coloreStampo(b.stampo);
                    const left = xOf(b.t0);
                    const w = Math.max(((b.t1 - b.t0) / DAY) * pxDay, 26);
                    return (
                      <div key={b.id}>
                        {setupPrima && (
                          <div className="absolute top-2 h-8 w-1.5 rounded-sm" style={{ left: left - 3, background: "repeating-linear-gradient(45deg,#fee2e2,#fee2e2 2px,#fff 2px,#fff 4px)", border: "1px solid #fca5a5" }} />
                        )}
                        <div
                          onClick={() => onSel(sel === b.id ? null : b.id)}
                          className={cn("absolute top-2 h-8 cursor-pointer overflow-hidden rounded-md border px-1.5 py-0.5 shadow-sm", sel === b.id && "ring-2 ring-primary ring-offset-1")}
                          style={{ left, width: w, background: col.bg, borderColor: col.border, color: col.text }}
                          title={`${b.code} · ${b.desc ?? ""}`}
                        >
                          <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-md" style={{ background: col.border }} />
                          <div className="truncate pl-1 text-[10.5px] font-semibold leading-tight">{b.code}</div>
                          {pxDay > 120 && <div className="truncate pl-1 text-[8.5px] opacity-80">{b.desc}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Overview({ origine, fine, winStart, winEnd, onJump }: { origine: number; fine: number; winStart: number; winEnd: number; onJump: (t: number) => void }) {
  const span = Math.max(fine - origine, DAY);
  const left = ((winStart - origine) / span) * 100;
  const w = ((winEnd - winStart) / span) * 100;
  return (
    <div
      className="relative ml-auto h-7 min-w-[180px] flex-1 cursor-pointer overflow-hidden rounded-md border bg-muted"
      onClick={(e) => {
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const frac = (e.clientX - r.left) / r.width;
        onJump(origine + frac * span - (winEnd - winStart) / 2);
      }}
      title="Clicca per spostare la finestra"
    >
      <div className="absolute inset-y-0 rounded-sm bg-primary/30 ring-1 ring-primary" style={{ left: `${Math.max(0, left)}%`, width: `${Math.min(100, w)}%` }} />
      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[9.5px] text-muted-foreground">{fmtDay(origine)}</span>
      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9.5px] text-muted-foreground">{fmtDay(fine)}</span>
    </div>
  );
}

function fmtDay(t: number): string {
  const d = new Date(t);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtRange(t0: number, t1: number): string {
  const f = (t: number) => {
    const d = new Date(t);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  return `${f(t0)} → ${f(t1)}`;
}
